type RoleUser = {
  user_role?: string | { name?: string; [key: string]: unknown }
  user_role_name?: string
  role_name?: string
  role?: string
} | null

/**
 * Normalize role labels so "Web Admin", "web_admin", and "web-admin" all match.
 */
export function normalizeRoleKey(role: string | null | undefined): string | null {
  if (!role || typeof role !== 'string') return null
  const key = role.toLowerCase().replace(/[\s_-]+/g, '')
  return key || null
}

/**
 * Get the role name from the user object.
 * Backend may return user_role as a JSON string (e.g. {"id":1,"name":"Requestor",...})
 * or as an object. This normalizes to the role name string.
 */
export function getRoleName(user: RoleUser): string | null {
  if (!user?.user_role) return null
  const ur = user.user_role
  if (typeof ur === 'string') {
    try {
      const parsed = JSON.parse(ur) as { name?: string }
      return parsed?.name ?? null
    } catch {
      return ur
    }
  }
  return (ur as { name?: string })?.name ?? null
}

export function isRequestorRole(user: RoleUser): boolean {
  return normalizeRoleKey(getRoleNameFromUser(user)) === 'requestor'
}

export function isTechnicianRole(user: RoleUser): boolean {
  return normalizeRoleKey(getRoleNameFromUser(user)) === 'technician'
}

/** Normalized keys: "Web Admin" / web_admin / web-admin → webadmin */
const CONTACT_READONLY_CREATE_ROLE_NAMES = new Set([
  'technician',
  'requestor',
  'agent',
  'webadmin',
  'approver',
])

const STAFF_REQUESTER_AUTOFILL_ROLE_NAMES = new Set([
  'technician',
  'agent',
  'webadmin',
  'approver',
])

/** Create Ticket: contact name/email are read-only for these roles. */
export function hasContactReadOnlyCreateRole(user: RoleUser): boolean {
  const key = normalizeRoleKey(getRoleNameFromUser(user))
  return key != null && CONTACT_READONLY_CREATE_ROLE_NAMES.has(key)
}

/** Create Ticket: staff roles that auto-fill contact from the selected requester. */
export function hasStaffRequesterAutofillRole(user: RoleUser): boolean {
  const key = normalizeRoleKey(getRoleNameFromUser(user))
  return key != null && STAFF_REQUESTER_AUTOFILL_ROLE_NAMES.has(key)
}

function getRoleNameFromUser(user: RoleUser): string | null {
  return (
    getRoleName(user) ??
    (typeof user?.user_role_name === 'string' ? user.user_role_name : null) ??
    (typeof user?.role_name === 'string' ? user.role_name : null) ??
    (typeof user?.role === 'string' ? user.role : null)
  )
}
